export interface ChatNotificationData {
  type?: string;
  caseId?: string;
  senderId?: string;
  senderName?: string;
  receiverId?: string;
  receiverName?: string;
  groupId?: string;
  groupName?: string;
  caseName?: string;
  isGeneralChat?: boolean | string;
  isGroup?: boolean | string;
  isOrgLevelMessageAll?: boolean | string;
  prefecture?: string;
  sourceCaseId?: string;
}

const isTrueFlag = (value: unknown): boolean => value === true || value === 'true';

export function normalizeChatNotification(data: ChatNotificationData = {}): ChatNotificationData {
  const normalized = { ...data };
  const isGeneralChat = isTrueFlag(normalized.isGeneralChat)
    || (normalized.caseId && normalized.receiverId && normalized.caseId === normalized.receiverId);
  const isOrgLevel = isGeneralChat && (!normalized.caseId || normalized.caseId === 'null' || normalized.caseId === 'undefined');

  if (isGeneralChat && normalized.isGeneralChat !== 'true') {
    normalized.isGeneralChat = 'true';
  }
  if (isOrgLevel) {
    normalized.isOrgLevelMessageAll = 'true';
    normalized.caseName = normalized.caseName || 'Message All';
    normalized.receiverName = normalized.receiverName || 'Message All';
  }
  return normalized;
}

export function chatThreadKey(data: ChatNotificationData = {}): string {
  const d = normalizeChatNotification(data);
  const isGeneral = isTrueFlag(d.isGeneralChat);
  const isGroup = isTrueFlag(d.isGroup);
  const isOrgLevel = isTrueFlag(d.isOrgLevelMessageAll)
    || (isGeneral && (!d.caseId || d.caseId === '' || d.caseId === 'null'));

  if (isOrgLevel) {
    const orgId = d.receiverId || '';
    const pref = d.prefecture || '';
    return pref ? `org-general:${orgId}:${pref}` : `org-general:${orgId}`;
  }
  if (isGeneral) {
    // A case has exactly one general chat, so caseId alone identifies the thread.
    return `case-general:${d.caseId || ''}`;
  }
  if (isGroup) {
    return `group:${d.caseId || ''}:${d.groupId || d.receiverId || ''}`;
  }
  // Direct message: the notification carries senderId=peer / receiverId=self, while an
  // open chat registers senderId=self / receiverId=peer. Sort both participants so the
  // key is direction-agnostic and matches from either side (#471).
  const dmPeers = [d.senderId, d.receiverId]
    .filter(Boolean)
    .map(String)
    .sort()
    .join('-');
  return `dm:${d.caseId || ''}:${dmPeers}`;
}

export function buildChatNotificationUrl(data: ChatNotificationData = {}, orgId?: string): string {
  const d = normalizeChatNotification(data);
  const params = new URLSearchParams();
  params.set('key', 'chat');
  params.set('timestamp', String(Date.now()));

  const isGeneral = isTrueFlag(d.isGeneralChat);
  const isGroup = isTrueFlag(d.isGroup);
  const isOrgLevel = isTrueFlag(d.isOrgLevelMessageAll)
    || (isGeneral && (!d.caseId || d.caseId === '' || d.caseId === 'null'));

  if (isOrgLevel) {
    const senderId = d.receiverId || orgId || d.senderId || '';
    params.set('senderId', senderId);
    params.set('senderName', d.receiverName || d.caseName || 'Message All');
    params.set('type', 'general');
    if (d.sourceCaseId) {
      return `/case/${encodeURIComponent(d.sourceCaseId)}?${params.toString()}`;
    }
    // Legacy fallback: no source case is known for this org-level notification.
    // The router wildcard currently lands this on the cases list (imperfect but safe).
    return `/chat?${params.toString()}`;
  }

  if (isGeneral && d.caseId) {
    // Case Message All is keyed by the case itself. Legacy notifications can
    // still carry the retired general-chat channel in receiverId.
    params.set('senderId', d.caseId);
    params.set('senderName', d.receiverName || d.caseName || 'Message All');
    params.set('type', 'general');
    return `/case/${encodeURIComponent(d.caseId)}?${params.toString()}`;
  }

  if (isGroup && d.caseId) {
    params.set('senderId', d.groupId || d.receiverId || '');
    params.set('senderName', d.groupName || d.receiverName || '');
    params.set('type', 'group');
    return `/case/${encodeURIComponent(d.caseId)}?${params.toString()}`;
  }

  if (d.caseId) {
    params.set('senderId', d.senderId || '');
    params.set('senderName', d.senderName || '');
    params.set('type', 'single');
    return `/case/${encodeURIComponent(d.caseId)}?${params.toString()}`;
  }

  params.set('senderId', d.senderId || '');
  params.set('senderName', d.senderName || '');
  params.set('type', 'single');
  return `/global-chat/${encodeURIComponent(d.senderId || '')}`;
}
